import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Lets a shopper add the display case alongside a domino set, straight from
 * the product page.
 *
 * The case is a real product variant (not a $1 fee stand-in), so its price,
 * inventory and taxes are exactly what checkout already knows. It rides in as
 * a second line the same way the riser configurator's surcharge does: the
 * main add-to-cart submit is captured first, and the case is only added once
 * that item has actually landed in the cart — so a failed add never leaves an
 * orphaned case behind, and a failed case add rolls the main item back out
 * rather than charging for dominoes alone when the shopper asked for both.
 *
 * @extends Component
 */
class DominoCaseAddon extends Component {
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {Array<{id: number, title: string, price: number, available: boolean}>} */
  #variants = [];
  /** Case variant id waiting to be added once the main item lands in the cart. */
  #pendingCaseVariantId = null;
  #pendingCaseQty = 0;
  /** Guards against reacting to the cart update our own case add triggers. */
  #applyingCase = false;
  /** The variant and quantity just added, so they can be rolled back. */
  #pendingMainVariantId = null;
  #pendingMainQty = 0;

  connectedCallback() {
    super.connectedCallback();

    const formId = this.dataset.formId;
    this.#form = formId ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId)) : null;
    this.#variants = this.#parseConfig();

    this.addEventListener('change', this.#onChange);
    this.#form?.addEventListener('submit', this.#onSubmit, { capture: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    this.#updateFee();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#form?.removeEventListener('submit', this.#onSubmit, { capture: true });
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #parseConfig() {
    const script = this.querySelector('script[data-dca-config]');
    if (!(script instanceof HTMLScriptElement)) return [];
    try {
      const parsed = JSON.parse(script.textContent ?? '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('[domino-case-addon] could not read the case config', error);
      return [];
    }
  }

  #toggle() {
    return this.querySelector('[data-dca-toggle]');
  }

  #checked() {
    const toggle = this.#toggle();
    return toggle instanceof HTMLInputElement && toggle.checked;
  }

  /** The color radio the shopper picked, when there is more than one case variant. */
  #selectedColorId() {
    const checked = this.querySelector('[data-dca-color]:checked');
    return checked instanceof HTMLInputElement ? Number(checked.value) : null;
  }

  /** The single variant id when the case has no color choice. */
  #singleVariantId() {
    const input = this.querySelector('[data-dca-single-variant]');
    return input instanceof HTMLInputElement ? Number(input.value) : null;
  }

  #selectedVariant() {
    const colors = this.querySelectorAll('[data-dca-color]');
    const id = colors.length > 0 ? this.#selectedColorId() : this.#singleVariantId();
    if (!id) return null;
    return this.#variants.find((v) => v.id === id) ?? null;
  }

  #onChange = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.matches('[data-dca-toggle]')) {
      const colors = this.querySelector('[data-dca-colors]');
      if (colors instanceof HTMLElement) colors.hidden = !this.#checked();
      this.#hideError();
    }

    this.#updateFee();
  };

  #updateFee() {
    const fee = this.querySelector('[data-dca-fee]');
    if (!(fee instanceof HTMLElement)) return;
    const variant = this.#selectedVariant() ?? this.#variants.find((v) => v.available) ?? this.#variants[0];
    if (variant) fee.textContent = `+${this.#money(variant.price)}`;
  }

  #quantity() {
    const input = this.#form?.querySelector('[name="quantity"]');
    if (input instanceof HTMLInputElement) {
      return Math.max(1, Number(input.value) || 1);
    }
    return 1;
  }

  #onSubmit = (/** @type {Event} */ event) => {
    // This runs in the capture phase ahead of the theme's own submit handler,
    // so anything unexpected in here must never be able to block a sale.
    try {
      this.#guardSubmit(event);
    } catch (error) {
      console.error('[domino-case-addon] submit guard failed, letting the form through', error);
    }
  };

  #guardSubmit(/** @type {Event} */ event) {
    this.#pendingCaseQty = 0;
    this.#pendingCaseVariantId = null;

    if (!this.#checked()) return;

    const variant = this.#selectedVariant();

    if (!variant || variant.available === false) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showError(
        this.querySelectorAll('[data-dca-color]').length > 0
          ? this.dataset.colorRequiredNote
          : this.dataset.soldOutNote
      );
      return;
    }

    this.#hideError();
    this.#pendingCaseVariantId = variant.id;
    this.#pendingCaseQty = this.#quantity();
    this.#pendingMainVariantId = Number(this.#form?.querySelector('[name="id"]')?.value) || null;
    this.#pendingMainQty = this.#quantity();
  }

  #onCartUpdate = (/** @type {Event} */ event) => {
    if (this.#applyingCase || this.#pendingCaseQty <= 0) return;
    if (/** @type {any} */ (event).detail?.data?.didError) {
      this.#pendingCaseQty = 0;
      return;
    }

    const variantId = this.#pendingCaseVariantId;
    const quantity = this.#pendingCaseQty;
    const mainVariantId = this.#pendingMainVariantId;
    const mainQty = this.#pendingMainQty;
    this.#pendingCaseQty = 0;
    this.#pendingCaseVariantId = null;
    this.#pendingMainVariantId = null;
    this.#pendingMainQty = 0;

    if (variantId) this.#addCase(variantId, quantity, mainVariantId, mainQty);
  };

  async #addCase(variantId, quantity, mainVariantId, mainQty) {
    this.#applyingCase = true;

    try {
      const response = await fetch(`${this.#root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity,
              properties: { For: this.dataset.productTitle || '' },
            },
          ],
        }),
      });

      if (!response.ok) throw new Error(`Case add failed with ${response.status}`);

      document.dispatchEvent(new CartUpdateEvent({}, 'domino-case-addon', { source: 'domino-case-addon' }));
    } catch (error) {
      // The case could not be added, so the set must not stay in the cart
      // alone when the shopper asked for both.
      console.error('[domino-case-addon] could not add the display case to the cart', error);
      await this.#rollBackMainItem(mainVariantId, mainQty);
      this.#showError(this.dataset.errorNote);
    } finally {
      this.#applyingCase = false;
    }
  }

  #root() {
    return window.Shopify?.routes?.root ?? '/';
  }

  /**
   * Takes back the quantity that was just added, leaving anything the
   * shopper had in the cart beforehand untouched.
   */
  async #rollBackMainItem(variantId, quantity) {
    if (!variantId || quantity <= 0) return;

    try {
      const cartResponse = await fetch(`${this.#root()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(`Cart read failed with ${cartResponse.status}`);

      const cart = await cartResponse.json();
      const line = [...cart.items].reverse().find((item) => item.variant_id === variantId);
      if (!line) return;

      const response = await fetch(`${this.#root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity - quantity) }),
      });

      if (!response.ok) throw new Error(`Rollback failed with ${response.status}`);

      document.dispatchEvent(new CartUpdateEvent({}, 'domino-case-addon', { source: 'domino-case-addon' }));
    } catch (error) {
      console.error('[domino-case-addon] could not roll the item back out of the cart', error);
    }
  }

  #money(cents) {
    const currency = this.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-dca-error]');
    if (error instanceof HTMLElement && message) {
      error.textContent = message;
      error.hidden = false;
      error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  #hideError() {
    const error = this.querySelector('[data-dca-error]');
    if (error instanceof HTMLElement) error.hidden = true;
  }
}

if (!customElements.get('domino-case-addon-component')) {
  customElements.define('domino-case-addon-component', DominoCaseAddon);
}
